import { useFrame, useThree } from "@react-three/fiber";
import { type ReactNode, type RefObject, useEffect, useRef } from "react";
import type * as THREE from "three";
import { rotationRadiansY } from "../../domain/viewer/saunaRotation";
import { createRotationSettleTracker } from "./rotationSettleTracker";

interface SaunaRigProps {
	/** Whole-building orientation in degrees; 0/90/180/270 once committed. */
	rotationDeg: number;
	rigRef: RefObject<THREE.Group | null>;
	/**
	 * Fired once per committed rotation, on the first frame where the group's
	 * `rotation.y` has actually reached the requested angle. See the note on
	 * timing below for why this cannot be an effect.
	 */
	onRotationSettled?: (rig: THREE.Group) => void;
	children: ReactNode;
}

/**
 * The one node every model-relative object hangs off.
 *
 * Turning the building is a single write to this group's `rotation.y` — the
 * built sauna group (attached imperatively through `SaunaModel`'s
 * `parentRef`), the god rays spilling out of its windows, the measurement
 * guides and badges, the heater-clearance envelope and the drag handles all
 * ride it. Anything that belongs to the SITE rather than the building —
 * ground, lake, sun, contact shadows — is deliberately left outside: the
 * point of the feature is turning the building relative to the plot.
 *
 * It is its own component, rather than a bare `<group>` inside SceneCanvas,
 * because the viewer's DOM-level tests mock the R3F reconciler; a component
 * can be mocked away the same way every other scene leaf here already is.
 *
 * ## Why the re-measure lives here, and in a frame callback
 *
 * A rotation moves geometry without REBUILDING it, so `SaunaModel`'s
 * dirty-tracked `onSceneChanged` never fires — but the contact shadow is a
 * one-shot bake and the sun's shadow camera is fitted once, both from
 * world-space bounds. Somebody has to re-measure.
 *
 * The trap is doing it from an effect in the OUTER React tree. `<Canvas>`
 * renders its children into a separate R3F root (`await root.configure(...)`
 * then `root.render(children)`), so the child tree's commit — where
 * `rotation-y` is actually written to the group — lands at least a microtask
 * and a scheduler lane AFTER the outer tree's effects have run. An outer
 * effect therefore measures the group at its OLD angle and produces a
 * bit-identical box, which `setModelBounds`' equality check then discards:
 * the shadow frustum silently keeps the previous orientation. `useLayoutEffect`
 * does not help — it is the wrong root, not the wrong phase.
 *
 * So: an effect only marks the rotation as pending, and a frame callback does
 * the measuring once it can SEE the transform on the group. That also keeps
 * a live drag out of it — the gesture writes `rotation.y` imperatively without
 * touching the prop, so nothing is pending and no per-frame re-bake happens
 * until the drag commits.
 */
export function SaunaRig({
	rotationDeg,
	rigRef,
	onRotationSettled,
	children,
}: SaunaRigProps) {
	const invalidate = useThree((state) => state.invalidate);
	const trackerRef = useRef<ReturnType<
		typeof createRotationSettleTracker
	> | null>(null);
	trackerRef.current ??= createRotationSettleTracker(rotationDeg);
	const onRotationSettledRef = useRef(onRotationSettled);
	onRotationSettledRef.current = onRotationSettled;

	useEffect(() => {
		if (!trackerRef.current?.request(rotationDeg)) return;
		// Demand frameloop: R3F invalidates on the `rotation-y` prop change
		// itself, but asking explicitly costs nothing and removes the
		// dependency on that implementation detail.
		invalidate();
	}, [rotationDeg, invalidate]);

	useFrame(() => {
		const rig = rigRef.current;
		if (!rig) return;
		if (!trackerRef.current?.settle(rig.rotation.y)) return;
		rig.updateWorldMatrix(true, true);
		onRotationSettledRef.current?.(rig);
	});

	return (
		<group
			name="sauna-rig"
			ref={rigRef}
			rotation-y={rotationRadiansY(rotationDeg)}
		>
			{children}
		</group>
	);
}
